import { gql } from "graphql-request";
import {
  AutocompleteInput,
  BooleanInput,
  Button,
  FunctionField,
  Labeled,
  Link,
  ReferenceInput,
  Show,
  SimpleForm,
  TabbedShowLayout,
  TextField,
  useNotify,
  useRecordContext,
  useRefresh,
  useUpdate,
} from "react-admin";
import { useMutation, useQuery } from "react-query";

import InfoTwoToneIcon from "@mui/icons-material/InfoTwoTone";
import MonetizationOnTwoToneIcon from "@mui/icons-material/MonetizationOnTwoTone";
import PhoneIphoneTwoToneIcon from "@mui/icons-material/PhoneIphoneTwoTone";
import SportsEsportsTwoToneIcon from "@mui/icons-material/SportsEsportsTwoTone";
import {
  Autocomplete,
  Chip,
  Grid,
  IconButton,
  TextField as MuiTextField,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { Check, Close, Sync, ViewTimelineTwoTone } from "@mui/icons-material";
import React, { useEffect, useState } from "react";

import { graphQLClient } from "../api";
import EmailWithCopy from "../components/EmailWithCopy";
import LocalisedDateField from "../components/localisedDateField";

const UserDetailTitle = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <span>
      User - {record.firstName} {record.lastName}
    </span>
  );
};

const SummaryTabHeader = () => {
  return (
    <span style={{ display: "" }}>
      <InfoTwoToneIcon /> <span style={{ verticalAlign: "super" }}>Summary</span>
    </span>
  );
};

const SubscriptionsTabHeader = () => {
  return (
    <span style={{ display: "" }}>
      <MonetizationOnTwoToneIcon /> <span style={{ verticalAlign: "super" }}>Subscriptions</span>
    </span>
  );
};

const AppDeviceTabHeader = () => {
  return (
    <span style={{ display: "" }}>
      <PhoneIphoneTwoToneIcon /> <span style={{ verticalAlign: "super" }}>App & Device</span>
    </span>
  );
};

const GamesTabHeader = () => {
  return (
    <span style={{ display: "" }}>
      <SportsEsportsTwoToneIcon /> <span style={{ verticalAlign: "super" }}>Games</span>
    </span>
  );
};

const ActivityTabHeader = () => {
  return (
    <span style={{ display: "" }}>
      <ViewTimelineTwoTone />
      <span style={{ verticalAlign: "super" }}>Activity</span>
    </span>
  );
};

const SendCardUpdateEmailAction = () => {
  const record = useRecordContext<any>();
  const notify = useNotify();
  const [overrideEmail, setOverrideEmail] = useState("");

  const mutation = useMutation(
    async (vars: { stripeCustomerId: string; overrideEmail?: string }) => {
      const res = await graphQLClient.request(
        gql`
          mutation SendCardUpdateEmail($stripeCustomerId: String!, $overrideEmail: String) {
            usersSendCardUpdateEmail(stripeCustomerId: $stripeCustomerId, overrideEmail: $overrideEmail) {
              status
              message
            }
          }
        `,
        vars
      );
      return res.usersSendCardUpdateEmail;
    },
    {
      onSuccess: (data: any) => {
        notify(data.message, { type: data.status ? "success" : "warning" });
      },
      onError: (err: any) => {
        notify(err.message || "Failed to send card update email", { type: "error" });
      },
    }
  );

  const stripeCustomerId = record?.subscriptions?.[0]?.stripeCustomerId;
  if (!stripeCustomerId) return <span>n/a</span>;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        maxWidth: 520,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Send Stripe Card Update Email
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
        Leave override blank to send to the user’s current email.
      </Typography>

      <Stack direction="row" spacing={1.25} alignItems="center">
        <MuiTextField
          fullWidth
          size="small"
          label="Override email (optional)"
          value={overrideEmail}
          onChange={(e) => setOverrideEmail(e.target.value)}
        />

        <Button
          label={mutation.isLoading ? "Sending..." : "Send"}
          onClick={() =>
            mutation.mutate({
              stripeCustomerId,
              overrideEmail: overrideEmail.trim() || undefined,
            })
          }
          disabled={mutation.isLoading}
          variant="contained"
          size="small"
          sx={{
            whiteSpace: "nowrap",
            minWidth: 92,
            height: 40,
          }}
        />
      </Stack>
    </Paper>
  );
};

const MarketoLeadSync = () => {
  const record = useRecordContext<any>();
  const notify = useNotify();
  const refresh = useRefresh();

  const mutation = useMutation(
    async (vars: { id: string }) => {
      const res = await graphQLClient.request(
        gql`
          mutation SyncMarketoLead($id: String!) {
            usersSyncMarketoLead(id: $id) {
              status
              message
            }
          }
        `,
        vars
      );
      return res.usersSyncMarketoLead;
    },
    {
      onSuccess: (data: any) => {
        notify(data.message, { type: data.status ? "success" : "warning" });
        if (data.status) {
          refresh();
        }
      },
      onError: (err: any) => {
        notify(err.message || "Failed to sync Marketo lead", { type: "error" });
      },
    }
  );

  if (!record) return null;

  return (
    <Tooltip title="Sync lead to Marketo">
      <span>
        <IconButton
          size="small"
          sx={{ ml: 0.5 }}
          onClick={() => mutation.mutate({ id: record.id })}
          disabled={mutation.isLoading || !record.id}
        >
          <Sync
            fontSize="inherit"
            sx={
              mutation.isLoading
                ? {
                    animation: "marketo-sync-spin 1s linear infinite",
                    "@keyframes marketo-sync-spin": {
                      from: { transform: "rotate(0deg)" },
                      to: { transform: "rotate(360deg)" },
                    },
                  }
                : undefined
            }
          />
        </IconButton>
      </span>
    </Tooltip>
  );
};

interface InlineCountryInlineProps {
  source?: string;
  label?: string;
}

const countryFilterToQuery = (query: string) => {
  if (query.length === 2) {
    return { id__exact: query };
  }
  return { name__icontains: query };
};

const CountryInlineEdit = ({ source = "country", label = "Country" }: InlineCountryInlineProps) => {
  const record = useRecordContext<any>();
  const notify = useNotify();
  const [update] = useUpdate();

  if (!record) return null;

  const handleChange = (countryId: string) => {
    if (!countryId || countryId === record[source]) return;
    update(
      "users",
      { id: record.id, data: { [source]: countryId }, previousData: record },
      {
        onSuccess: () => notify("Country updated", { type: "success" }),
        onError: (err: any) => notify(err?.message || "Failed to update country", { type: "error" }),
      }
    );
  };

  return (
    <SimpleForm onSubmit={() => {}} toolbar={false}>
      <ReferenceInput source={source} reference="allCountries">
        <AutocompleteInput
          label={label}
          optionText={(o: any) => `${o.flag} ${o.id} - ${o.name}`}
          filterToQuery={countryFilterToQuery}
          sx={{ width: 300 }}
          onChange={(value) => handleChange(value as string)}
        />
      </ReferenceInput>
    </SimpleForm>
  );
};

interface InlineBooleanSwitchProps {
  source: string;
  label?: string;
}

const TestUserInlineEdit = ({ source }: InlineBooleanSwitchProps) => {
  const record = useRecordContext<any>();
  const notify = useNotify();

  const [update] = useUpdate();

  if (!record) return null;

  const handleChange = (newValue: boolean) => {
    update(
      "users",
      { id: record.id, data: { [source]: newValue }, previousData: record },
      {
        onSuccess: () => notify("User test status changed", { type: "success" }),
        onError: (err: any) => notify(err?.message || "Failed to update user test status", { type: "error" }),
      }
    );
  };

  return (
    <SimpleForm onSubmit={() => {}} toolbar={false}>
      <BooleanInput source={source} label="Test User" onChange={(e) => handleChange(e.target.checked)} />
    </SimpleForm>
  );
};

const MarketingOptInInlineEdit = ({ source }: InlineBooleanSwitchProps) => {
  const record = useRecordContext<any>();
  const notify = useNotify();

  const [update] = useUpdate();

  if (!record) return null;

  const handleChange = (newValue: boolean) => {
    update(
      "users",
      { id: record.id, data: { [source]: newValue }, previousData: record },
      {
        onSuccess: () => notify("Marketing Opt-in status changed for user", { type: "success" }),
        onError: (err: any) => notify(err?.message || "Failed to update marketing opt-in status", { type: "error" }),
      }
    );
  };

  return (
    <SimpleForm onSubmit={() => {}} toolbar={false}>
      <BooleanInput source={source} label="Marketing Opt-in" onChange={(e) => handleChange(e.target.checked)} />
    </SimpleForm>
  );
};

interface UserLocaleInlineEditProps {
  source?: string;
}

const UserLocaleInlineEdit = ({ source = "locale" }: UserLocaleInlineEditProps) => {
  const record = useRecordContext<any>();
  const notify = useNotify();
  const [update] = useUpdate();

  if (!record) return null;

  const handleChange = (localeId: string) => {
    if (!localeId || localeId === record[source]) return;
    update(
      "users",
      { id: record.id, data: { [source]: localeId }, previousData: record },
      {
        onSuccess: () => notify("Locale updated", { type: "success" }),
        onError: (err: any) => notify(err?.message || "Failed to update user locale", { type: "error" }),
      }
    );
  };

  return (
    <SimpleForm onSubmit={() => {}} toolbar={false}>
      <ReferenceInput source={source} reference="allLocales">
        <AutocompleteInput
          label={source}
          sx={{ width: 300 }}
          optionText={(o: any) => `${o.id} - ${o.name}`}
          onChange={(value: string) => handleChange(value as string)}
        />
      </ReferenceInput>
    </SimpleForm>
  );
};

interface LanguageInlineEditProps {
  source?: string;
}

const languageFilterToQuery = (query: string) => {
  if (query.length === 2) {
    return { id__exact: query };
  } else {
    return { name__icontains: query };
  }
};

const LanguageInlineEdit = ({ source = "language" }: LanguageInlineEditProps) => {
  const record = useRecordContext<any>();
  const notify = useNotify();
  const [update] = useUpdate();

  if (!record) return null;

  const handleChange = (languageId: string) => {
    // allLanguages ids are lowercase (e.g. "en"); the user record stores the
    // uppercase code (e.g. "EN") like country does, so normalise before saving.
    const normalised = languageId?.toUpperCase();
    if (!normalised || normalised === record[source]) return;

    update(
      "users",
      { id: record.id, data: { [source]: normalised }, previousData: record },
      {
        onSuccess: () => notify("Successfully updated the user's language", { type: "success" }),
        onError: (err: any) => notify(err?.message || "Failed to update the user's language", { type: "error" }),
      }
    );
  };

  return (
    <SimpleForm onSubmit={() => {}} toolbar={false}>
      <ReferenceInput source={source} reference="allLanguages" perPage={100}>
        <AutocompleteInput
          label={source}
          sx={{ width: 300 }}
          optionText={(o: any) => `${o.id} - ${o.name}`}
          // record stores "EN" but option ids are "en" — lowercase so the selection matches
          format={(v: string) => (v ? v.toLowerCase() : "")}
          onChange={(value: string) => handleChange(value as string)}
        />
      </ReferenceInput>
    </SimpleForm>
  );
};

type FeatureFlagOption = {
  name: string;
  enabled: boolean;
  emails: string[];
  localeIds: string[];
};

const isEnabledGlobalFlag = (flag: FeatureFlagOption) => flag.enabled && !flag.emails.length && !flag.localeIds.length;

const UserFeatureFlags = () => {
  const record = useRecordContext<any>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [overrideIds, setOverrideIds] = useState<string[]>([]);

  useEffect(() => {
    setOverrideIds(record?.featureFlagOverrideIds || []);
  }, [record?.featureFlagOverrideIds]);

  const optionsQuery = useQuery<FeatureFlagOption[]>("feature-flag-options", async () => {
    const response = await graphQLClient.request(
      gql`
        query FeatureFlagOptions {
          featureFlags {
            name
            enabled
            emails
            localeIds
          }
        }
      `
    );
    return (response.featureFlags || []).filter(Boolean);
  });

  const mutation = useMutation(
    async (featureFlagIds: string[]) => {
      const response = await graphQLClient.request(
        gql`
          mutation UpdateUserFeatureFlagOverrides($userId: String!, $featureFlagIds: [String!]!) {
            featureFlagsUpdateUserOverrides(userId: $userId, featureFlagIds: $featureFlagIds) {
              featureFlagIds
              effectiveFeatureFlagIds
            }
          }
        `,
        { userId: record.id, featureFlagIds }
      );
      return response.featureFlagsUpdateUserOverrides;
    },
    {
      onSuccess: (data: any) => {
        setOverrideIds(data.featureFlagIds || []);
        notify("Feature flag overrides updated", { type: "success" });
        refresh();
      },
      onError: (error: any) => {
        notify(error.message || "Failed to update feature flag overrides", { type: "error" });
      },
    }
  );

  if (!record) return null;

  const options = optionsQuery.data || [];
  const selectedOptions = overrideIds.map(
    (id) => options.find((option) => option.name === id) || { name: id, enabled: true, emails: [], localeIds: [] }
  );
  const isProtectedLastOverride = (flag: FeatureFlagOption) =>
    flag.enabled &&
    !flag.localeIds.length &&
    flag.emails.length === 1 &&
    flag.emails[0].trim().toLowerCase() === record.email.trim().toLowerCase();

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2, maxWidth: 760 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Feature Flags
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Effective flags include global availability, country availability and direct email overrides.
      </Typography>
      <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1, mb: 2 }}>
        {(record.effectiveFeatureFlagIds || []).length ? (
          record.effectiveFeatureFlagIds.map((name: string) => <Chip key={name} label={name} color="success" />)
        ) : (
          <Typography variant="body2">No effective feature flags</Typography>
        )}
      </Stack>

      <Autocomplete
        multiple
        options={options}
        value={selectedOptions}
        loading={optionsQuery.isLoading}
        isOptionEqualToValue={(option, value) => option.name === value.name}
        getOptionDisabled={(option) => isEnabledGlobalFlag(option) || isProtectedLastOverride(option)}
        getOptionLabel={(option) =>
          `${option.name}${
            isEnabledGlobalFlag(option)
              ? " (globally available)"
              : isProtectedLastOverride(option)
              ? " (last audience; manage on feature flag page)"
              : option.enabled
              ? ""
              : " (disabled)"
          }`
        }
        onChange={(_, selected) => setOverrideIds(selected.map((option) => option.name))}
        renderInput={(params) => (
          <MuiTextField
            {...params}
            label="Direct email overrides"
            helperText="Global flags are managed on the feature flag page. Audience changes that would accidentally globalise or restrict a flag are blocked."
          />
        )}
      />
      <Button
        label={mutation.isLoading ? "Saving..." : "Save overrides"}
        onClick={() => mutation.mutate(overrideIds)}
        disabled={mutation.isLoading || optionsQuery.isLoading}
        variant="contained"
        sx={{ mt: 2 }}
      />
    </Paper>
  );
};

const LazyLoadedUserActivityList = React.lazy(() => import("../components/UserActivityList"));
const LazyLoadedUserProfilesList = React.lazy(() => import("../components/UserProfilesList"));
const LazyLoadedUserSubscriptionList = React.lazy(() => import("../list/subscriptions"));
const LazyLoadedUserFreeSubscriptionList = React.lazy(() => import("../list/freeSubscriptions"));

const UserDetail = () => {
  return (
    <Show title={<UserDetailTitle />}>
      <TabbedShowLayout>
        <TabbedShowLayout.Tab label={<SummaryTabHeader />}>
          <Grid container columnGap={4}>
            <Grid item>
              <Labeled label="First Name">
                <TextField source="firstName" />
              </Labeled>
            </Grid>
            <Grid item>
              <Labeled label="Last Name">
                <TextField source="lastName" />
              </Labeled>
            </Grid>
          </Grid>
          <Grid container columnGap={4}>
            <Grid item>
              <Labeled
                label={
                  <FunctionField
                    render={(record: any) => {
                      let label = record.confirmed ? "Confirmed" : "Unconfirmed";
                      return (
                        <>
                          <small>Email</small>
                          <Tooltip title={label}>
                            <IconButton
                              size="small"
                              sx={{ ml: 0.5 }} // small left margin for spacing
                            >
                              {label === "Confirmed" ? <Check fontSize="inherit" /> : <Close fontSize="inherit" />}
                            </IconButton>
                          </Tooltip>
                        </>
                      );
                    }}
                  />
                }
              >
                <EmailWithCopy />
              </Labeled>
            </Grid>
            <Grid item>
              <Labeled label="Old Email">
                <EmailWithCopy source="oldEmail" emptyText="never changed" />
              </Labeled>
            </Grid>
            <Grid item>
              <MarketingOptInInlineEdit source="marketingOptIn" />
            </Grid>
            <Grid item>
              <Labeled label="Marketing Opt-In">
                <FunctionField
                  render={(record: any) =>
                    record.marketingOptIn ? <Check fontSize="inherit" /> : <Close fontSize="inherit" />
                  }
                />
              </Labeled>
            </Grid>
            <Grid item>
              <Labeled label="Marketo Lead ID">
                <FunctionField
                  render={(record: any) =>
                    record.marketoLeadId ? (
                      <>
                        {record.marketoLeadId}
                        <Link
                          to={`https://app-nld104.marketo.com/leadDatabase/loadLeadDetail?leadId=${record.marketoLeadId}&accessZoneId=1`}
                          target="_blank"
                        >
                          {" ↗"}
                        </Link>
                      </>
                    ) : (
                      <Close fontSize="inherit" />
                    )
                  }
                />
              </Labeled>
            </Grid>
            <Grid item>
              <Labeled label="Marketo Sync At">
                <FunctionField
                  render={(record: any) =>
                    record.statistics?.marketoSyncAt ? (
                      <>
                        <LocalisedDateField record={record.statistics} field="marketoSyncAt" compact showTime />
                        <MarketoLeadSync />
                      </>
                    ) : (
                      <>
                        <Close fontSize="inherit" />
                        <MarketoLeadSync />
                      </>
                    )
                  }
                />
              </Labeled>
            </Grid>
          </Grid>
          <Grid container columnGap={4}>
            <Grid item>
              <CountryInlineEdit />
            </Grid>
            <Grid item>
              <TestUserInlineEdit source="isTest" />
            </Grid>
            <Grid item>
              <UserLocaleInlineEdit />
            </Grid>
            <Grid item>
              <LanguageInlineEdit />
            </Grid>
          </Grid>
          <Grid container columnGap={4}>
            <Grid item>
              <Labeled label="Orders">
                <FunctionField
                  label={"Orders"}
                  render={(user: any) => (
                    <Link
                      title="View orders"
                      to={`/orders?filter=${JSON.stringify({ email__icontains: encodeURIComponent(user.email) })}`}
                    >{`${user.statistics?.numberOfOrders || 0}`}</Link>
                  )}
                />
              </Labeled>
            </Grid>
            <Grid item>
              <Labeled label="GUM Playbrush">
                <FunctionField
                  render={(record: any) =>
                    record.statistics?.hasGumPlaybrush ? <Check fontSize="inherit" /> : <Close fontSize="inherit" />
                  }
                />
              </Labeled>
            </Grid>
            <Grid item>
              <Labeled label="GUM Smart One">
                <FunctionField
                  render={(record: any) =>
                    record.statistics?.hasGumSmartOne ? <Check fontSize="inherit" /> : <Close fontSize="inherit" />
                  }
                />
              </Labeled>
            </Grid>
            <Grid item>
              <Labeled label="Last Login">
                <FunctionField
                  render={(record: any) => {
                    return record.lastLoginAt ? (
                      <LocalisedDateField record={record} field="lastLogin" showTime />
                    ) : (
                      <span>n/a</span>
                    );
                  }}
                />
              </Labeled>
            </Grid>
          </Grid>

          <FunctionField
            label={"Customer Since"}
            render={(user: { createdAt: string }) => (
              <LocalisedDateField record={user} field={"createdAt"} showTime={true} />
            )}
          />

          <UserFeatureFlags />

          {/* <Grid>
          <Grid item>
            <Labeled label="Send Stripe Card Update Email">
              <SendCardUpdateEmailAction/>
            </Labeled>
          </Grid>
        </Grid> */}
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label={<SubscriptionsTabHeader />} path="subscriptions">
          {/*<LazyLoadedUserSubscriptionList />*/}
          <FunctionField
            label="Paid Subscriptions"
            render={(user: { email: string }) => (
              <>
                <LazyLoadedUserSubscriptionList filter={{ customer_email: user.email }} />
              </>
            )}
          />
          <FunctionField
            label="Free Subscription"
            render={(user: { email: string }) => (
              <>
                <LazyLoadedUserFreeSubscriptionList filter={{ user_email: user.email }} record={user} />
              </>
            )}
          />
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label={<AppDeviceTabHeader />} path="device">
          <FunctionField
            label="App Last Opened"
            render={(user: any) => {
              return user.statistics?.dateAppLastUsed ? (
                <LocalisedDateField record={user.statistics} field={"dateAppLastUsed"} showTime={true} />
              ) : (
                <small>Never Opened</small>
              );
            }}
          />
          <FunctionField
            label="Last Login"
            render={(user: { lastLoginAt: string }) => {
              return <LocalisedDateField record={user} field={"lastLogin"} showTime={true} />;
            }}
          />

          <TextField source="os" label="Operating System" />
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab path="games" label={<GamesTabHeader />}>
          {/*<small>🚧Coming soon!🚧</small>*/}
          <LazyLoadedUserProfilesList />
        </TabbedShowLayout.Tab>

        <TabbedShowLayout.Tab path="activity" label={<ActivityTabHeader />}>
          <LazyLoadedUserActivityList />
        </TabbedShowLayout.Tab>
      </TabbedShowLayout>
    </Show>
  );
};

export default UserDetail;
